import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { getAllOrgRoles, updateOrgMemberRole, type AppRole } from '../../services/appUser.service';
import { getOrgMembers, setMemberDisabled, type OrgMember } from '../../services/org.service';
import { Navbar } from '../../components/Navbar';
import { BackButton, Button, Input, Spinner, toastError, toastSuccess, InviteModal } from '../../components/core';
import { MembersTable } from './MembersTable';
import { MembersMobileList } from './MembersMobileList';

export default function MembersPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { org, orgId, orgSlug } = useOrg();
  const { t } = useTranslation();

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [toggling, setToggling] = useState<Record<string, boolean>>({});
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const filteredMembers = members.filter(m =>
    (m.displayName?.toLowerCase().includes(search.toLowerCase()) || false) ||
    m.email.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    async function load() {
      try {
        const [fetchedMembers, fetchedRoles] = await Promise.all([
          getOrgMembers(orgId),
          getAllOrgRoles(orgId),
        ]);
        setMembers(fetchedMembers);
        setRoles(fetchedRoles);
      } catch {
        setError(t('members.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [orgId, t]);

  async function handleToggleDisabled(uid: string, isDisabled: boolean) {
    // Prevent disabling the last active admin
    if (isDisabled) {
      const activeAdmins = members.filter(m => m.role === 'admin' && !m.isDisabled);
      if (activeAdmins.length === 1 && activeAdmins[0].uid === uid) {
        toastError(t('members.lastAdminError'));
        return;
      }
    }

    setToggling(prev => ({ ...prev, [uid]: true }));
    try {
      await setMemberDisabled(orgId, uid, isDisabled);
      setMembers(prev =>
        prev.map(m =>
          m.uid === uid ? { ...m, isDisabled } : m
        )
      );
      toastSuccess(t('members.toastSaved'));
    } catch {
      toastError(t('members.saveError'));
    } finally {
      setToggling(prev => { const next = { ...prev }; delete next[uid]; return next; });
    }
  }

  async function handleChangeRole(uid: string, roleId: string) {
    // Prevent demoting the last active admin
    if (roleId !== 'admin') {
      const activeAdmins = members.filter(m => m.role === 'admin' && !m.isDisabled);
      if (activeAdmins.length === 1 && activeAdmins[0].uid === uid) {
        toastError(t('members.lastAdminError'));
        return;
      }
    }

    setToggling(prev => ({ ...prev, [uid]: true }));
    try {
      await updateOrgMemberRole(orgId, uid, roleId);
      const role = roles.find(r => r.id === roleId);
      const permissions = role?.permissions ?? [];
      setMembers(prev =>
        prev.map(m =>
          m.uid === uid ? { ...m, role: roleId, permissions } : m
        )
      );
      toastSuccess(t('members.toastSaved'));
    } catch {
      toastError(t('members.saveError'));
    } finally {
      setToggling(prev => { const next = { ...prev }; delete next[uid]; return next; });
    }
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <BackButton />

        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <h1 className="font-display text-xl font-semibold text-gray-900">{t('members.title')}</h1>
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-800">
                {members.length}
              </span>
            </div>
            <p className="font-body text-sm text-gray-500">{t('members.subtitle')}</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/org/${orgSlug}/roles`)}
          >
            {t('members.manageRoles')}
          </Button>
        </div>

        <div className="mb-4 flex gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">⌕</span>
            <Input
              type="search"
              placeholder={t('members.searchPlaceholder')}
              className="ps-11"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsInviteModalOpen(true)}
          >
            {t('members.inviteButton')}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">{error}</div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-green-200 bg-white shadow-sm">
            <MembersTable
              filteredMembers={filteredMembers}
              members={members}
              selfUid={user?.uid}
              toggling={toggling}
              onToggleDisabled={handleToggleDisabled}
              roles={roles}
              onChangeRole={handleChangeRole}
            />
            <MembersMobileList
              filteredMembers={filteredMembers}
              members={members}
              selfUid={user?.uid}
              toggling={toggling}
              onToggleDisabled={handleToggleDisabled}
              roles={roles}
              onChangeRole={handleChangeRole}
            />
          </div>
        )}

        <InviteModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          orgId={orgId}
          currentUserId={user?.uid || ''}
          onInviteCreated={() => {
            toastSuccess(t('invites.created') || 'Invite generated');
          }}
        />
      </main>
    </div>
  );
}
